import type { Action } from '~/hooks/actions/types.ts';
import { isRecord } from '~/services/layout/layout.ts';

type PlaylistConfig = {
  localizations?: Record<string, unknown>;
  customConfiguration?: Record<string, unknown>;
};

function getPlaylistLocalization({
  playlistConfig,
  key,
}: {
  playlistConfig?: PlaylistConfig;
  key: string;
}): string | undefined {
  const localizationsSource = playlistConfig?.localizations;
  const localizations = isRecord(localizationsSource)
    ? localizationsSource
    : undefined;

  return getStringValue(localizations?.[key]);
}

/**
 * Returns the first value that is a non-blank string.
 * @param values - Candidate values checked in order.
 * @returns The first non-blank string (untrimmed), or undefined when none match.
 */
export function getStringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }

  return undefined;
}

function getLocalizationKeyByAlias(alias?: string): string | undefined {
  const normalizedAlias = alias?.trim().toLowerCase();

  if (!normalizedAlias) return undefined;

  const aliasToLocalizationKeyMap: Record<string, string> = {
    add_to_playlist: 'add_to_playlist_action_label',
    add_all_to_playlist: 'add_all_to_playlist_action_label',
    remove_from_playlist: 'remove_from_playlist_action_label',
    edit_order: 'edit_order_action_label',
    edit_name: 'edit_name_and_details_action_label',
    edit_name_and_details: 'edit_name_and_details_action_label',
    delete_playlist: 'delete_playlist_action_label',
    delete_collection: 'delete_playlist_action_label',
  };

  return aliasToLocalizationKeyMap[normalizedAlias];
}

function getAssetKeyByAlias({
  alias,
  icon,
  actionAssetFlavour,
}: {
  alias?: string;
  icon?: string;
  actionAssetFlavour: 'flavour_1' | 'flavour_2';
}): string | undefined {
  const normalizedAlias = alias?.trim().toLowerCase();
  const normalizedIcon = icon?.trim().toLowerCase();

  const flavorPrefix = `${actionAssetFlavour}_`;

  if (
    normalizedAlias === 'add_to_playlist' ||
    normalizedAlias === 'add_all_to_playlist'
  ) {
    if (
      normalizedIcon === 'next' ||
      normalizedIcon === 'chevron' ||
      normalizedIcon === 'chevron_right' ||
      normalizedIcon === 'chevron-right'
    ) {
      return `${flavorPrefix}add_to_playlist_chevron_asset`;
    }

    return `${flavorPrefix}add_to_playlist_asset`;
  }

  if (normalizedAlias === 'remove_from_playlist') {
    return `${flavorPrefix}remove_from_playlist_asset`;
  }

  if (normalizedAlias === 'edit_order') {
    return `${flavorPrefix}edit_order_asset`;
  }

  if (
    normalizedAlias === 'edit_name' ||
    normalizedAlias === 'edit_name_and_details'
  ) {
    return `${flavorPrefix}edit_name_and_details_asset`;
  }

  if (
    normalizedAlias === 'delete_collection' ||
    normalizedAlias === 'delete_playlist'
  ) {
    return `${flavorPrefix}delete_playlist_asset`;
  }

  return undefined;
}

const CREATE_NEW_PLAYLIST_ASSET_KEYS = {
  mobile: 'mobile_primary_button_create_new_playlist_asset',
  desktop: 'desktop_primary_button_create_new_playlist_asset',
} as const;

/**
 * Returns the configured "Create New" playlist button icon for the breakpoint,
 * or undefined when no asset is uploaded in the Playlists plugin.
 */
export function getCreateNewPlaylistAssetSrc({
  playlistConfig,
  isDesktop,
}: {
  playlistConfig?: PlaylistConfig;
  isDesktop: boolean;
}): string | undefined {
  return getStringValue(
    playlistConfig?.customConfiguration?.[
      CREATE_NEW_PLAYLIST_ASSET_KEYS[isDesktop ? 'desktop' : 'mobile']
    ]
  );
}

/**
 * Returns the configured playlist selection indicator asset for the breakpoint
 * and selection state, or undefined when no asset is uploaded in the Playlists plugin.
 */
export function getPlaylistSelectionAssetSrc({
  playlistConfig,
  isDesktop,
  isSelected,
}: {
  playlistConfig?: PlaylistConfig;
  isDesktop: boolean;
  isSelected: boolean;
}): string | undefined {
  const customConfigurationSource = playlistConfig?.customConfiguration;
  const customConfiguration = isRecord(customConfigurationSource)
    ? customConfigurationSource
    : undefined;
  const breakpointPrefix: string = isDesktop
    ? 'desktop_selection'
    : 'mobile_selection';
  const assetKey: string = isSelected
    ? `${breakpointPrefix}_checked_checkbox_asset`
    : `${breakpointPrefix}_checkbox_asset`;

  return getStringValue(customConfiguration?.[assetKey]);
}

export function resolvePlaylistActionAssetFlavour({
  source,
  uiComponentStyles,
}: {
  source: Record<string, unknown>;
  uiComponentStyles?: Record<string, unknown>;
}): 'flavour_1' | 'flavour_2' {
  const sourceButton = isRecord(source.button) ? source.button : undefined;

  const flavorValue = getStringValue(
    source.action_asset_flavour,
    source.action_asset_flavor,
    sourceButton?.action_asset_flavour,
    sourceButton?.action_asset_flavor,
    uiComponentStyles?.action_asset_flavour,
    uiComponentStyles?.action_asset_flavor
  );

  if (flavorValue?.trim().toLowerCase() === 'flavour_2') return 'flavour_2';

  return 'flavour_1';
}

export function getPlaylistAliasLabel({
  alias,
  playlistConfig,
}: {
  alias?: string;
  playlistConfig?: PlaylistConfig;
}): string | undefined {
  const localizationKey = getLocalizationKeyByAlias(alias);

  if (!localizationKey) return undefined;

  const localizationsSource = playlistConfig?.localizations;
  const localizations = isRecord(localizationsSource)
    ? localizationsSource
    : undefined;

  return getStringValue(localizations?.[localizationKey]);
}

export function getPlaylistAliasAsset({
  alias,
  icon,
  actionAssetFlavour,
  playlistConfig,
}: {
  alias?: string;
  icon?: string;
  actionAssetFlavour: 'flavour_1' | 'flavour_2';
  playlistConfig?: PlaylistConfig;
}): string | undefined {
  const assetKey = getAssetKeyByAlias({
    alias,
    icon,
    actionAssetFlavour,
  });

  if (!assetKey) return undefined;

  const customConfigurationSource = playlistConfig?.customConfiguration;
  const customConfiguration = isRecord(customConfigurationSource)
    ? customConfigurationSource
    : undefined;

  return getStringValue(customConfiguration?.[assetKey]);
}

function getEntryId(entry?: Record<string, unknown>): string | undefined {
  return getStringValue(entry?.id, entry?.identifier);
}

function getEntryTitle(entry?: Record<string, unknown>): string | undefined {
  return getStringValue(entry?.title, entry?.name);
}

function getCollectionSelectorAction({
  itemId,
  playlistConfig,
}: {
  itemId: string;
  playlistConfig?: PlaylistConfig;
}): Action {
  const title = getPlaylistLocalization({
    playlistConfig,
    key: 'add_to_playlist_title',
  });

  const recentPlaylistsSectionTitle = getPlaylistLocalization({
    playlistConfig,
    key: 'recent_playlists_section_title',
  });

  return {
    type: 'openBottomSheet',
    options: {
      modal_presentation: {
        type: 'bottom_sheet',
        style_variant: 'modal_bottom_sheet',
      },
      header: {
        title,
      },
      content: {
        title: recentPlaylistsSectionTitle,
        itemsUrl: `/user/collections?item_id=${encodeURIComponent(itemId)}`,
        items: [],
      },
    },
  } as Action;
}

function getEditableCollectionAction({
  collectionId,
  title,
}: {
  collectionId: string;
  title?: string;
}): Action {
  return {
    type: 'openBottomSheet',
    options: {
      modal_presentation: {
        type: 'bottom_sheet',
        style_variant: 'modal_bottom_sheet',
      },
      header: {
        title: 'Edit Playlist',
        subtitle: title,
      },
      content: {
        title: title || 'Edit Playlist',
        itemsUrl: `/user/collections/${encodeURIComponent(
          collectionId
        )}?editable=true`,
        items: [],
      },
    },
  } as Action;
}

function getEditCollectionNameAction({
  collectionId,
  title,
}: {
  collectionId: string;
  title?: string;
}): Action {
  return {
    type: 'editCollectionName',
    options: {
      collectionId,
      name: title,
    },
  } as Action;
}

function getDeleteCollectionActions({
  collectionId,
  playlistName,
  playlistConfig,
}: {
  collectionId: string;
  playlistName?: string;
  playlistConfig?: PlaylistConfig;
}): Action[] {
  const confirmationTitle = getPlaylistLocalization({
    playlistConfig,
    key: 'delete_playlist_confirmation_title',
  });

  const confirmationDescription = getPlaylistLocalization({
    playlistConfig,
    key: 'delete_playlist_confirmation_description',
  });

  const cancelButtonLabel = getPlaylistLocalization({
    playlistConfig,
    key: 'delete_playlist_cancel_button_label',
  });

  const confirmButtonLabel = getPlaylistLocalization({
    playlistConfig,
    key: 'delete_playlist_confirm_button_label',
  });

  const resolvedDescription = confirmationDescription?.replace(
    '{Playlist Name}',
    playlistName || ''
  );

  return [
    {
      type: 'confirmDialog',
      options: {
        title: confirmationTitle,
        message: resolvedDescription,
        okButtonText: confirmButtonLabel,
        cancelButtonText: cancelButtonLabel,
      },
    } as Action,
    {
      type: 'sendCloudEvent',
      options: {
        inflateData: true,
        url: '@{ctx/cloud_events_url}',
        type: 'com.applicaster.collection.delete.v1',
        subject: 'collection_mutation',
        data: {
          collectionId,
        },
      },
    } as Action,
    {
      type: 'refreshComponent',
      options: {},
    } as Action,
  ];
}

export function getDefaultPlaylistActions({
  alias,
  sourceEntry,
  playlistConfig,
}: {
  alias?: string;
  sourceEntry?: Record<string, unknown>;
  playlistConfig?: PlaylistConfig;
}): Action[] {
  const normalizedAlias = alias?.trim().toLowerCase();

  if (!normalizedAlias) return [];

  if (
    normalizedAlias === 'add_to_playlist' ||
    normalizedAlias === 'add_all_to_playlist'
  ) {
    const itemId = getEntryId(sourceEntry);

    if (!itemId) return [];

    return [
      getCollectionSelectorAction({
        itemId,
        playlistConfig,
      }),
    ];
  }

  const collectionId = getEntryId(sourceEntry);
  const collectionTitle = getEntryTitle(sourceEntry);

  if (!collectionId) return [];

  if (normalizedAlias === 'edit') {
    return [
      getEditableCollectionAction({
        collectionId,
        title: collectionTitle,
      }),
    ];
  }

  if (
    normalizedAlias === 'edit_name' ||
    normalizedAlias === 'edit_name_and_details'
  ) {
    return [
      getEditCollectionNameAction({
        collectionId,
        title: collectionTitle,
      }),
    ];
  }

  if (
    normalizedAlias === 'delete_collection' ||
    normalizedAlias === 'delete_playlist'
  ) {
    return getDeleteCollectionActions({
      collectionId,
      playlistName: collectionTitle,
      playlistConfig,
    });
  }

  return [];
}
